import React from 'react';

import { Search, Trash } from 'lucide-react';

import { IdeaFieldTypes, MaxFieldOptionCharLength, MaxFieldOptions } from 'common/constants/ideas';
import TextInput from 'common/inputs/TextInput';
import { FieldTypeDataMap } from 'common/subdomain/admin/v2/constants';
import ButtonV2 from 'common/ui/ButtonV2';
import IconButtonV2 from 'common/ui/IconButtonV2';
import SingleSelectWithSearch from 'common/ui/SingleSelectWithSearch';
import { Span } from 'common/ui/Text';
import styles from 'css-module/components/subdomain/admin/v2/_AdminV2CustomIdeaFieldModal.module.scss';

import FormField from './FormField';

import type {
  CustomFieldFormState,
  FieldFormState,
} from 'common/subdomain/admin/v2/hooks/useIdeaFieldForm';
import type { Option } from 'common/ui/common/select/SelectCommon';

type BaseFieldInputsProps = {
  formState: FieldFormState;
  setValue: (field: keyof CustomFieldFormState, value: string | string[]) => void;
  types?: IdeaFieldTypes[];
  readOnly: boolean;
  loading: boolean;
};

const TypeOptions: Option[] = Object.keys(FieldTypeDataMap).map((type) => ({
  label: FieldTypeDataMap[type as IdeaFieldTypes].label,
  value: type,
  prefixIcon: FieldTypeDataMap[type as IdeaFieldTypes].icon,
}));

export default function BaseFieldInputs({
  types = Object.values(IdeaFieldTypes),
  formState,
  setValue,
  readOnly,
  loading,
}: BaseFieldInputsProps) {
  const selectedTypeOption = TypeOptions.find((option) => option.value === formState.type);

  const showOptionsInput =
    formState.type === IdeaFieldTypes.dropdown || formState.type === IdeaFieldTypes.multiselect;

  const getAutoFocusIndex = () => {
    if (
      formState.type === IdeaFieldTypes.dropdown ||
      formState.type === IdeaFieldTypes.multiselect
    ) {
      return (formState.options?.length ?? 0) - 1;
    }

    return null;
  };

  const handleOptionChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!('options' in formState)) {
      return;
    }
    const existingOptions = formState.options ?? [];
    existingOptions[index] = e.target.value;
    setValue('options', existingOptions);
  };

  const handleDeleteOption = (index: number) => () => {
    if (!('options' in formState)) {
      return;
    }
    const existingOptions = formState.options ?? [];
    setValue(
      'options',
      existingOptions.filter((_, idx) => idx !== index),
    );
  };

  const handleAddOption = () => {
    if (!('options' in formState)) {
      return;
    }
    const existingOptions = formState.options ?? [];
    setValue('options', [...existingOptions, '']);
  };

  return (
    <>
      <FormField label="Field Name" required>
        <TextInput
          autoFocus
          className={styles.nameInput}
          id="fieldName"
          inset={<Search className={styles.searchIcon} size={16} />}
          onChange={(e) => setValue('name', e.target.value)}
          placeholder="Field name"
          value={formState.name}
        />
      </FormField>

      <FormField label="Type" required>
        <SingleSelectWithSearch
          disabled={readOnly}
          onChange={(option) => setValue('type', option?.value ?? '')}
          options={TypeOptions.filter((option) => types.includes(option.value as IdeaFieldTypes))}
          placeholder="Field type"
          selectOptionsClassName={styles.fieldTypeOptions}
          value={selectedTypeOption}
        />
      </FormField>

      {showOptionsInput && (
        <FormField label="Options">
          <ul className={styles.optionsList}>
            {formState.options?.map((option, index) => (
              <li key={index} className={styles.optionItem}>
                <Span className={styles.optionItemCount}>{index + 1}</Span>
                <TextInput
                  autoFocus={index === getAutoFocusIndex()}
                  className={styles.optionItemInput}
                  onChange={handleOptionChange(index)}
                  maxLength={MaxFieldOptionCharLength}
                  placeholder="Option name"
                  value={option}
                />
                <IconButtonV2
                  aria-label="Remove option"
                  color="error"
                  icon={Trash}
                  onClick={handleDeleteOption(index)}
                  size="small"
                  variant="plain"
                />
              </li>
            ))}
          </ul>
          <ButtonV2
            variant="plain"
            disabled={(formState.options?.length ?? 0) >= MaxFieldOptions || loading}
            onClick={handleAddOption}
            size="small">
            Create new option
          </ButtonV2>
        </FormField>
      )}
    </>
  );
}
